import { useEffect } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { FC } from 'react';

import { PageHeader } from '@wf/sw-ui-kit';
import { Mode } from '@wf/components';

import { selectedLicenseTypeTransitionsSelector } from '../../../License/store/selectors';
import DocumentHistoryTable from '../../components/DocumentHistoryTable';
import { setSelectedLicenseTypeId } from '../../../License/store/slice';
import ContentContainer from '../../../../components/ContentContainer';
import { getLicenseTypeById } from '../../../License/store/actions';
import LicenseStatus from '../../../../components/LicenseStatus';

import useUpdateAccess from '../../../../hooks/useUpdateAccess';
import useRedirect from '../../../../hooks/useRedirect';

import { documentHistorySelector } from '../../../../store/audit/selectors';
import { resetDocumentHistory } from '../../../../store/audit/slices';
import { getDocumentHistory } from '../../../../store/audit/actions';

const DocumentHistory: FC = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: documentHistory, licenceTypeId } = useSelector(documentHistorySelector);
  const { state } = useLocation();

  const hasUpdateAccess = useUpdateAccess();
  const selectedLicenseTypeTransitions = useSelector(selectedLicenseTypeTransitionsSelector);

  useRedirect();

  const dataCount = documentHistory.length;
  const currentStatus = documentHistory[0]?.status;

  const lastOperationName = documentHistory[dataCount - 1]?.operation || '';

  const ogaStatus = selectedLicenseTypeTransitions[lastOperationName]?.operationStatus || null;
  const ogaStatusInNationalLang =
    selectedLicenseTypeTransitions[lastOperationName]?.operationStatusInNationalLang || null;

  useEffect(() => {
    dispatch(getDocumentHistory(id as string));

    return () => {
      dispatch(resetDocumentHistory());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (licenceTypeId) {
      dispatch(getLicenseTypeById(licenceTypeId));
    }

    return () => {
      dispatch(setSelectedLicenseTypeId(null));
    };
  }, [dispatch, licenceTypeId]);

  const navigateBack = () =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    navigate(state?.from?.pathname ?? `../../${hasUpdateAccess ? 'edit' : 'view'}/${id as string}`, {
      relative: state?.from?.pathname ? 'route' : 'path',
    });

  return (
    <>
      <PageHeader
        tags={
          currentStatus && (
            <LicenseStatus
              ogaStatusInNationalLang={ogaStatusInNationalLang}
              status={currentStatus}
              ogaStatus={ogaStatus}
            />
          )
        }
        onNavigateBack={navigateBack}
        title={t('Document History')}
        mode={Mode.view}
      />
      <ContentContainer>
        <DocumentHistoryTable />
      </ContentContainer>
    </>
  );
};

export default DocumentHistory;
